import React, { useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Toaster } from './ui/sonner';
import { Tags } from 'lucide-react';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useChartPreferences } from '../context/ChartPreferencesContext';
import { useProject } from '../context/ProjectContext';
import { PaywallDialog } from './PaywallDialog';
import { cn } from '../lib/utils';
import ChatAssistant from './ChatAssistant';
import SupportButton from './SupportButton';
import DemoBanner from './DemoBanner';

export const Layout = () => {
  const location = useLocation();
  const { showLabels, toggleLabels, numberFormat, setNumberFormat, decimals, setDecimals } = useChartPreferences();
  const { showPaywall, setShowPaywall, currentProject } = useProject();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  
  // Get page title based on route
  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/app' || path === '/app/') return 'Gestor de Proyectos';
    if (path === '/app/settings') return 'Configuracion';
    if (path.includes('/dashboard')) return 'Dashboard';
    if (path.includes('/specifications')) return 'Especificaciones del Proyecto';
    if (path.includes('/investment')) return 'Inversión Necesaria';
    if (path.includes('/financing')) return 'Financiamiento';
    if (path.includes('/sales-volume')) return 'Volumen de Ventas';
    if (path.includes('/pricing')) return 'Precio y Costos de Venta';
    if (path.includes('/fixed-expenses')) return 'Gastos Fijos';
    if (path.includes('/income-statement')) return 'Estado de Resultados';
    if (path.includes('/cash-flow')) return 'Flujo de Efectivo';
    if (path.includes('/valuation')) return 'Valuación del Proyecto';
    if (path.includes('/scenarios')) return 'Escenarios y PDF';
    if (path.includes('/explained')) return 'Tu Proyecto Explicado';
    if (path.includes('/ai-modify')) return 'Modificar con IA';
    if (path.includes('/ai-analyst')) return 'Analista IA';
    return 'Valuation Pro';
  };

  return (
    <div className="min-h-screen bg-background">
      <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
      
      {/* Main Content */}
      <div className={`transition-all duration-300 ${sidebarCollapsed ? 'ml-16' : 'ml-64'}`}>
        {/* Top Header */}
        <header className="sticky top-0 z-40 glass border-b border-border/30">
          <div className="flex items-center justify-between px-8 py-4">
            <div className="flex items-center gap-4">
              <h2 className="text-lg font-semibold">{getPageTitle()}</h2>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Chart Labels Toggle */}
              <Button
                variant={showLabels ? "default" : "outline"}
                size="sm"
                onClick={toggleLabels}
                className={cn(
                  "gap-2 text-xs",
                  showLabels && "bg-primary text-primary-foreground"
                )}
                data-testid="toggle-labels-btn"
                title="Mostrar/Ocultar etiquetas en gráficas"
              >
                <Tags className="w-4 h-4" />
                {showLabels ? 'Etiquetas ON' : 'Etiquetas OFF'}
              </Button>

              {/* Number Format Select - Only show when labels are ON */}
              {showLabels && (
                <>
                  <Select value={numberFormat} onValueChange={setNumberFormat}>
                    <SelectTrigger className="w-28 h-8 text-xs" data-testid="number-format-select">
                      <SelectValue placeholder="Formato" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Auto</SelectItem>
                      <SelectItem value="k">Miles (k)</SelectItem>
                      <SelectItem value="M">Millones (M)</SelectItem>
                      <SelectItem value="full">Completo</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select value={String(decimals)} onValueChange={(v) => setDecimals(parseInt(v))}>
                    <SelectTrigger className="w-24 h-8 text-xs" data-testid="decimals-select">
                      <SelectValue placeholder="Decimales" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">0 dec</SelectItem>
                      <SelectItem value="1">1 dec</SelectItem>
                      <SelectItem value="2">2 dec</SelectItem>
                    </SelectContent>
                  </Select>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-8">
          {currentProject?.is_demo && location.pathname.includes('/project/') && <DemoBanner />}
          <Outlet />
        </main>
      </div>
      
      <Toaster position="top-right" richColors />
      <PaywallDialog open={showPaywall} onOpenChange={setShowPaywall} />
      <ChatAssistant />
      <SupportButton />
    </div>
  );
};
